import {
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Popover,
  Typography,
  Card,
  Box,
  useTheme,
} from "@mui/material";
import { ArrowDropDown, ContentCopyOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useState } from "react";
import "./VpnDetails.scss";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import vpnLoader from "../../../assets/baseVersion/vpn-loader.svg";
import DangerousIcon from "@mui/icons-material/Dangerous";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VpnActions from "./VpnActions";
import { ROLES_CONTENT_PROPS } from "../../../shared/constants/constant-data";
import useRolePermissions from "../../../shared/hooks/useRolePermissions";

const VpnDetails = ({ vpnDetails, callBaseVPN }: any) => {
  const [openTestDialog, setOpenTestDialog] = useState(false);
  const [testIp, setTestIp] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCloseButton, setShowCloseButton] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [testResult, setTestResult] = useState<null | {
    success: boolean;
    message: string;
  }>(null);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { hasPermissions } = useRolePermissions();
  const getScanStatusClass = (value: any): string => {
    const arr = ["low", "critical", "partial", "cancle", "in-pgr"];
    return arr[value];
  };
  const handleActionsNow = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const open = Boolean(anchorEl);

  const handlePaauseVpnCall = async (payload: any) => {
    console.log("Pause VPN called", payload);
    try {
      const response = await axiosInstance.post(
        END_POINTS.CONNECTION_STATUS,
        payload
      );
      if (response.status === 200) {
        toast(response.data.message, { type: "success" });
        await callBaseVPN();
      }
    } catch (error: any) {
      console.error("Error adding agent:", error.response);
      toast.error(error.response?.data?.message || "Something went wrong", {
        type: "error",
      });
    }
  };

  const handleResumeVpnCall = async (payload: any) => {
    console.log("resumeVpn VPN called", payload);
    try {
      const response = await axiosInstance.post(
        END_POINTS.CONNECTION_STATUS,
        payload
      );
      if (response.status === 200) {
        toast(response.data.message, { type: "success" });
        await callBaseVPN();
      }
    } catch (error: any) {
      console.error("Error adding agent:", error.response);
      toast.error(error.response?.data?.message || "Something went wrong", {
        type: "error",
      });
    }
  };

  const handleDeleteVpnCall = async (payload: any) => {
    console.log("Delete VPN called", payload);
    try {
      const url = `${END_POINTS.VPN}/${payload}`;
      const response = await axiosInstance.delete(url);
      if (response.status === 200) {
        await callBaseVPN();
        toast.success(response?.data?.message || "Something went wrong", {
          type: "success",
        });
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Something went wrong", {
        type: "error",
      });
    }
  };

  const handleOpenTestDialog = () => {
    setOpenTestDialog(true);
    setLoading(false);
    setShowCloseButton(false);
  };

  const handleCloseTestDialog = () => {
    setOpenTestDialog(false);
    setTestIp("");
    setTestResult(null);
  };

  const handleTest = async () => {
    const dataToSend = {
      customer_private_ip: testIp,
      tunnel_id: vpnDetails?.tunnel_id,
    };
    setLoading(true);
    setShowCloseButton(false);
    setTestResult(null);
    try {
      const response = await axiosInstance.post(
        END_POINTS.VPN_TEST,
        dataToSend
      );
      if (response.status === 200) {
        console.log(response?.data?.data);
        toast.success(response.data.message, { type: "success" });
        if (response.data?.data?.vpn_tunnel_status) {
          setTestResult({
            success: true,
            message: vpnDetails.tunnel_description,
          });
        } else {
          setTestResult({
            success: false,
            message: vpnDetails.tunnel_description,
          });
        }
        setShowCloseButton(true);
      }
      setShowCloseButton(true);
      setLoading(false);
    } catch (error: any) {
      console.error("Error testing VPN:", error.response);
      setTestResult({
        success: false,
        message: "An error occurred while testing the VPN",
      });
      setShowCloseButton(true);
      setLoading(false);
      toast.error(error.response?.data?.message || "Something went wrong", {
        type: "error",
      });
    }
  };
  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success("Content copied to clipboard!");
      })
      .catch((err) => {
        toast.error("Failed to copy content.");
      });
  };

  const formatDate = (input: string | number | Date): string => {
    const date = new Date(input);
    const pad = (n: number) => String(n).padStart(2, "0");

    const day = pad(date.getDate());
    const month = date.toLocaleString("en-US", { month: "long" }); 
    const year = date.getFullYear();
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <>
      <div
        className={`vpn-details-container ${
          isDarkMode ? "dark-mode" : "light-mode"
        }`}
      >
        <div
          className={`vpn-data-header ${
            isDarkMode ? "dark-mode" : "light-mode"
          }`}
        >
          <h3
            style={{
              margin: "0",
              fontSize: "15px",
              fontWeight: "700",
              lineHeight: "20px",
              color: isDarkMode ? "#ffffff" : "#000000",
            }}
          >
            VPN Details
          </h3>
          <div
            className={`vpn-btns ${isDarkMode ? "dark-mode" : "light-mode"}`}
          >
            <Button
              className="btn btn-outline"
              sx={{
                "&:focus": { outline: "none" },
                color: isDarkMode ? "#ffffff" : "#000000",
                borderColor: isDarkMode ? "#ffffff" : "#000000",
                "&:hover": {
                  borderColor: "#3A2ADA",
                  color: "#3A2ADA",
                },
              }}
              onClick={handleOpenTestDialog}
            >
              Test
            </Button>
            {(hasPermissions(ROLES_CONTENT_PROPS.CREATE_VPN) ||
              hasPermissions(ROLES_CONTENT_PROPS.DELETE_VPN)) && (
              <VpnActions
                vpnDetails={vpnDetails}
                handlePauseVpn={handlePaauseVpnCall}
                handleResumeVpn={handleResumeVpnCall}
                handleDeleteVpn={handleDeleteVpnCall}
                callBaseVPN={callBaseVPN}
              />
            )}
          </div>
        </div>
        <div className="vpn-data-inner">
          <div
            className={`right-inner scroll ${
              isDarkMode ? "dark-mode" : "light-mode"
            }`}
          >
            <div className="item">
              <p className={`text ${isDarkMode ? "dark-mode" : "light-mode"}`}>
                Tunnel Name
              </p>
              <p
                className={`sub-text ${
                  isDarkMode ? "dark-mode" : "light-mode"
                }`}
              >
                {vpnDetails.tunnel_name}
              </p>
            </div>
            <div className="item">
              <p className={`text ${isDarkMode ? "dark-mode" : "light-mode"}`}>
                Tunnel Description
              </p>
              <p
                className={`sub-text ${
                  isDarkMode ? "dark-mode" : "light-mode"
                }`}
              >
                {vpnDetails.tunnel_description}
              </p>
            </div>
            <div className="item">
              <p className={`text ${isDarkMode ? "dark-mode" : "light-mode"}`}>
                Client Gateway IP
              </p>
              <p
                className={`sub-text ${
                  isDarkMode ? "dark-mode" : "light-mode"
                }`}
              >
                {vpnDetails?.customer_private_ip}
              </p>
            </div>
            <div className="item">
              <p className={`text ${isDarkMode ? "dark-mode" : "light-mode"}`}>
                CIDR Block
              </p>
              <p
                className={`sub-text ${
                  isDarkMode ? "dark-mode" : "light-mode"
                }`}
              >
                {vpnDetails.client_cidr_block}
              </p>
            </div>
            <div className="item">
              <p className={`text ${isDarkMode ? "dark-mode" : "light-mode"}`}>
                Created By
              </p>
              <p
                className={`sub-text ${
                  isDarkMode ? "dark-mode" : "light-mode"
                }`}
              >
                {vpnDetails.firstname} {vpnDetails.lastname}
              </p>
            </div>

            <div className="item">
              <p className={`text ${isDarkMode ? "dark-mode" : "light-mode"}`}>
                Created Date & time
              </p>
              <p
                className={`sub-text ${
                  isDarkMode ? "dark-mode" : "light-mode"
                }`}
              >
                {vpnDetails.created_on
                  ? formatDate(vpnDetails.created_on)
                  : "N/A"}
              </p>
            </div>
            <div className="item">
              <p className={`text ${isDarkMode ? "dark-mode" : "light-mode"}`}>
                Last Successfully Connected
              </p>
              <p
                className={`sub-text ${
                  isDarkMode ? "dark-mode" : "light-mode"
                }`}
              >
                {vpnDetails.last_success_connection}
              </p>
            </div>
            <div className="item">
              <p className={`text ${isDarkMode ? "dark-mode" : "light-mode"}`}>
                Last Failed connection
              </p>
              <p
                className={`sub-text ${
                  isDarkMode ? "dark-mode" : "light-mode"
                }`}
              >
                {vpnDetails.last_failed_connection}
              </p>
            </div>
            <div className="item">
              <p className={`text ${isDarkMode ? "dark-mode" : "light-mode"}`}>
                ESP
              </p>
              <p
                className={`sub-text ${
                  isDarkMode ? "dark-mode" : "light-mode"
                }`}
              >
                {vpnDetails.details.esp}
              </p>
            </div>
            <div className="item">
              <p className={`text ${isDarkMode ? "dark-mode" : "light-mode"}`}>
                IKE
              </p>
              <p
                className={`sub-text ${
                  isDarkMode ? "dark-mode" : "light-mode"
                }`}
              >
                {vpnDetails.details.ike}
              </p>
            </div>
            <div className="item">
              <p className={`text ${isDarkMode ? "dark-mode" : "light-mode"}`}>
                Dpddelay
              </p>
              <p
                className={`sub-text ${
                  isDarkMode ? "dark-mode" : "light-mode"
                }`}
              >
                {vpnDetails.details.dpddelay}
              </p>
            </div>
            <div className="item">
              <p className={`text ${isDarkMode ? "dark-mode" : "light-mode"}`}>
                Lifetime
              </p>
              <p
                className={`sub-text ${
                  isDarkMode ? "dark-mode" : "light-mode"
                }`}
              >
                {vpnDetails.details.lifetime}
              </p>
            </div>
            <div className="item">
              <p className={`text ${isDarkMode ? "dark-mode" : "light-mode"}`}>
                Left Auth
              </p>
              <p
                className={`sub-text ${
                  isDarkMode ? "dark-mode" : "light-mode"
                }`}
              >
                {vpnDetails.details.leftauth}
              </p>
            </div>
            <div className="item">
              <p className={`text ${isDarkMode ? "dark-mode" : "light-mode"}`}>
                Right Auth
              </p>
              <p
                className={`sub-text ${
                  isDarkMode ? "dark-mode" : "light-mode"
                }`}
              >
                {vpnDetails.details.rightauth}
              </p>
            </div>
            <div className="item">
              <p className={`text ${isDarkMode ? "dark-mode" : "light-mode"}`}>
                DPDT Timeout
              </p>
              <p
                className={`sub-text ${
                  isDarkMode ? "dark-mode" : "light-mode"
                }`}
              >
                {vpnDetails.details.dpdtimeout}
              </p>
            </div>
            <div className="item">
              <p className={`text ${isDarkMode ? "dark-mode" : "light-mode"}`}>
                Left Subnet
              </p>
              <p
                className={`sub-text ${
                  isDarkMode ? "dark-mode" : "light-mode"
                }`}
              >
                {vpnDetails.details.leftsubnet}
              </p>
            </div>
            <div className="item">
              <p className={`text ${isDarkMode ? "dark-mode" : "light-mode"}`}>
                Right Subnet
              </p>
              <p
                className={`sub-text ${
                  isDarkMode ? "dark-mode" : "light-mode"
                }`}
              >
                {vpnDetails.details.rightsubnet}
              </p>
            </div>
            <div className="item">
              <p className={`text ${isDarkMode ? "dark-mode" : "light-mode"}`}>
                IKE Lifetime
              </p>
              <p
                className={`sub-text ${
                  isDarkMode ? "dark-mode" : "light-mode"
                }`}
              >
                {vpnDetails.details.ikelifetime}
              </p>
            </div>
            <div className="item">
              <p className={`text ${isDarkMode ? "dark-mode" : "light-mode"}`}>
                Key Exchange
              </p>
              <p
                className={`sub-text ${
                  isDarkMode ? "dark-mode" : "light-mode"
                }`}
              >
                {vpnDetails.details.keyexchange}
              </p>
            </div>
          </div>
          <div className="left-inner">
            <div
              className={`header1 ${isDarkMode ? "dark-mode" : "light-mode"}`}
            >
              <h4
                style={{
                  margin: "0",
                  fontWeight: "700",
                  fontSize: "14px",
                  color: isDarkMode ? "#ffffff" : "#000000",
                }}
              >
                Tunnel Details
              </h4>
              {vpnDetails.paused ? (
                <span
                  className={`vulnerab_label partial ${
                    isDarkMode ? "dark-mode" : "light-mode"
                  }`}
                >
                  Paused
                </span>
              ) : (
                <span
                  className={`vulnerab_label ${getScanStatusClass(
                    vpnDetails.status
                  )} ${isDarkMode ? "dark-mode" : "light-mode"}`}
                >
                  {vpnDetails.tunnel_status}
                </span>
              )}
            </div>
            <div
              style={{
                margin: "10px",
              }}
            >
              <TextField
                label="SPARK VPN Public IP"
                fullWidth
                className="mb_3"
                value={vpnDetails.spark_vpn_public_id}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: isDarkMode ? "#333333" : "#d3d3d3",
                    },
                    "&:hover fieldset": { borderColor: "#3A2ADA" },
                    backgroundColor: isDarkMode ? "#191C24" : "#ffffff",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  },
                  "& .MuiInputLabel-root": {
                    color: isDarkMode ? "#ffffff" : "#000000",
                  },
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <ContentCopyOutlined
                          style={{
                            cursor: "pointer",
                            color: isDarkMode ? "#ffffff" : "#000000",
                          }}
                          onClick={() =>
                            handleCopyToClipboard(
                              vpnDetails.spark_vpn_public_id
                            )
                          }
                        />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <TextField
                label="SPARK Subnet"
                fullWidth
                className="mb_3"
                value={vpnDetails.spark_subnet}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: isDarkMode ? "#333333" : "#d3d3d3",
                    },
                    "&:hover fieldset": { borderColor: "#3A2ADA" },
                    backgroundColor: isDarkMode ? "#191C24" : "#ffffff",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  },
                  "& .MuiInputLabel-root": {
                    color: isDarkMode ? "#ffffff" : "#000000",
                  },
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <ContentCopyOutlined
                          style={{
                            cursor: "pointer",
                            color: isDarkMode ? "#ffffff" : "#000000",
                          }}
                          onClick={() =>
                            handleCopyToClipboard(vpnDetails.spark_subnet)
                          }
                        />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <TextField
                label="SIKE Key"
                fullWidth
                className="mb_3"
                value={vpnDetails.ike_key}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: isDarkMode ? "#333333" : "#d3d3d3",
                    },
                    "&:hover fieldset": { borderColor: "#3A2ADA" },
                    backgroundColor: isDarkMode ? "#191C24" : "#ffffff",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  },
                  "& .MuiInputLabel-root": {
                    color: isDarkMode ? "#ffffff" : "#000000",
                  },
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <ContentCopyOutlined
                          style={{
                            cursor: "pointer",
                            color: isDarkMode ? "#ffffff" : "#000000",
                          }}
                          onClick={() =>
                            handleCopyToClipboard(vpnDetails.ike_key)
                          }
                        />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <Dialog
        open={openTestDialog}
        onClose={handleCloseTestDialog}
        sx={{
          "& .MuiDialog-paper": {
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "360px",
            height: testResult ? "250px" : "210px", // Adjust height based on test result
          },
        }}
      >
        <DialogTitle className="dialog-title">Insert an IP to test</DialogTitle>
        <DialogContent>
          <TextField
            label="Enter IP"
            fullWidth
            value={testIp}
            onChange={(e) => setTestIp(e.target.value)}
            required
            sx={{
              marginTop: "11px",
            }}
          />
          {testResult && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginTop: "10px",
                gap: "10px",
              }}
            >
              {testResult.success ? (
                <>
                  <CheckCircleIcon sx={{ color: "#00DF80" }} />
                  <div className="test-success">
                    <p
                      style={{ margin: 0, fontWeight: "700", fontSize: "14px" }}
                    >
                      Test Successful
                    </p>
                    <p className="test-description">{testResult.message}</p>
                  </div>
                </>
              ) : (
                <>
                  <DangerousIcon sx={{ color: "#F04248" }} />
                  <div className="test-failed">
                    <p>Test Failed</p>
                    <p className="test-description">{testResult.message}</p>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            gap: "10px",
            justifyContent: "center",
            marginBottom: "10px",
          }}
        >
          {loading ? (
            <img className="spin-loader" src={vpnLoader} alt="Loading..." />
          ) : showCloseButton ? (
            <Button
              sx={{ width: "120px", color: "#696595", borderColor: "#696595" }}
              variant="outlined"
              onClick={handleCloseTestDialog}
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                sx={{
                  width: "120px",
                  color: "#696595",
                  borderColor: "#696595",
                }}
                variant="outlined"
                onClick={handleCloseTestDialog}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className="test-button"
                onClick={handleTest}
              >
                Test
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VpnDetails;
